/**
 * Base E-commerce Tracker Class
 * All platform-specific trackers extend this class
 */
export default class BaseEcommerceTracker {
  constructor(b2mTracker, platformName) {
    this.b2mTracker = b2mTracker;
    this.platformName = platformName;
    this.isInitialized = false;
    this.observedElements = new Set();
    this.dataLayerObserver = null;
    
    // Standard e-commerce events
    this.eventTypes = {
      VIEW_ITEM: 'view_item',
      VIEW_ITEM_LIST: 'view_item_list',
      SELECT_ITEM: 'select_item',
      ADD_TO_CART: 'add_to_cart',
      REMOVE_FROM_CART: 'remove_from_cart',
      VIEW_CART: 'view_cart',
      BEGIN_CHECKOUT: 'begin_checkout',
      ADD_SHIPPING_INFO: 'add_shipping_info',
      ADD_PAYMENT_INFO: 'add_payment_info',
      PURCHASE: 'purchase',
      REFUND: 'refund',
      ADD_TO_WISHLIST: 'add_to_wishlist',
      REMOVE_FROM_WISHLIST: 'remove_from_wishlist',
      SEARCH: 'search',
      VIEW_PROMOTION: 'view_promotion',
      SELECT_PROMOTION: 'select_promotion'
    };
  }

  /**
   * Initialize the tracker - override in subclasses
   */
  init() {
    if (this.isInitialized) return;
    
    console.log(`[B2M ${this.platformName}] Initializing tracker...`);
    
    // Setup common tracking
    this.setupDataLayerObserver();
    this.setupDOMObserver();
    this.detectCurrentPage();
    
    this.isInitialized = true;
    console.log(`[B2M ${this.platformName}] ✅ Tracker initialized`);
  }

  /**
   * Setup dataLayer observer for GTM/GA events
   */
  setupDataLayerObserver() {
    // Initialize dataLayer if not exists
    window.dataLayer = window.dataLayer || [];
    
    // Store original push method
    const originalPush = window.dataLayer.push.bind(window.dataLayer);
    
    // Override push to intercept events
    window.dataLayer.push = (...args) => {
      // Process each pushed item
      args.forEach(item => {
        this.processDataLayerEvent(item);
      });
      
      // Call original push
      return originalPush(...args);
    };
    
    // Process existing dataLayer items
    window.dataLayer.forEach(item => {
      this.processDataLayerEvent(item);
    });
    
    console.log(`[B2M ${this.platformName}] DataLayer observer active`);
  }

  /**
   * Process dataLayer events - override in subclasses for platform-specific handling
   */
  processDataLayerEvent(data) {
    if (!data || typeof data !== 'object') return;
    
    // Handle standard GA4 e-commerce events
    if (data.event) {
      this.handleStandardEvent(data);
    }
    
    // Handle ecommerce object (enhanced ecommerce)
    if (data.ecommerce) {
      this.handleEnhancedEcommerceEvent(data);
    }
  }

  /**
   * Handle standard GA4 events
   */
  handleStandardEvent(data) {
    const eventName = data.event;
    const ecommerce = data.ecommerce || {};
    
    switch (eventName) {
      case 'view_item':
        this.trackViewItem(ecommerce.items || []);
        break;
      case 'view_item_list':
        this.trackViewItemList(ecommerce.items || [], ecommerce.item_list_name);
        break;
      case 'select_item':
        this.trackSelectItem(ecommerce.items || []);
        break;
      case 'add_to_cart':
        this.trackAddToCart(ecommerce.items || [], ecommerce.value, ecommerce.currency);
        break;
      case 'remove_from_cart':
        this.trackRemoveFromCart(ecommerce.items || []);
        break;
      case 'view_cart':
        this.trackViewCart(ecommerce.items || [], ecommerce.value, ecommerce.currency);
        break;
      case 'begin_checkout':
        this.trackBeginCheckout(ecommerce.items || [], ecommerce.value, ecommerce.currency);
        break;
      case 'add_shipping_info':
        this.trackAddShippingInfo(ecommerce);
        break;
      case 'add_payment_info':
        this.trackAddPaymentInfo(ecommerce);
        break;
      case 'purchase':
        this.trackPurchase(ecommerce);
        break;
      case 'refund':
        this.trackRefund(ecommerce);
        break;
    }
  }

  /**
   * Handle enhanced ecommerce events (legacy format)
   */
  handleEnhancedEcommerceEvent(data) {
    const ecommerce = data.ecommerce;
    
    if (ecommerce.impressions) {
      this.trackViewItemList(this.normalizeItems(ecommerce.impressions));
    }
    if (ecommerce.detail?.products) {
      this.trackViewItem(this.normalizeItems(ecommerce.detail.products));
    }
    if (ecommerce.click?.products) {
      this.trackSelectItem(this.normalizeItems(ecommerce.click.products));
    }
    if (ecommerce.add?.products) {
      this.trackAddToCart(this.normalizeItems(ecommerce.add.products));
    }
    if (ecommerce.remove?.products) {
      this.trackRemoveFromCart(this.normalizeItems(ecommerce.remove.products));
    }
    if (ecommerce.checkout?.products) {
      this.trackBeginCheckout(this.normalizeItems(ecommerce.checkout.products));
    }
    if (ecommerce.purchase?.products) {
      this.trackPurchase({
        transaction_id: ecommerce.purchase.actionField?.id,
        value: ecommerce.purchase.actionField?.revenue,
        currency: ecommerce.currencyCode || 'TRY',
        items: this.normalizeItems(ecommerce.purchase.products)
      });
    }
  }

  /**
   * Setup DOM mutation observer for dynamic content
   */
  setupDOMObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.processNewElement(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Process newly added DOM elements - override in subclasses
   */
  processNewElement(element) {
    // Override in platform-specific trackers
  }

  /**
   * Detect current page type
   */
  detectCurrentPage() {
    const path = window.location.pathname;
    const pageType = this.getPageType(path);
    
    console.log(`[B2M ${this.platformName}] Page type: ${pageType}`);
    
    // Auto-track based on page type
    switch (pageType) {
      case 'product':
        this.autoTrackProductPage();
        break;
      case 'category':
        this.autoTrackCategoryPage();
        break;
      case 'cart':
        this.autoTrackCartPage();
        break;
      case 'checkout':
        this.autoTrackCheckoutPage();
        break;
      case 'order-confirmation':
        this.autoTrackOrderConfirmation();
        break;
      case 'search':
        this.autoTrackSearchPage();
        break;
    }
  }

  /**
   * Get page type from URL - override in subclasses
   */
  getPageType(path) {
    // Default patterns - override in platform-specific trackers
    if (path.includes('/product') || path.includes('/urun')) return 'product';
    if (path.includes('/category') || path.includes('/kategori')) return 'category';
    if (path.includes('/cart') || path.includes('/sepet')) return 'cart';
    if (path.includes('/checkout') || path.includes('/odeme')) return 'checkout';
    if (path.includes('/order-confirmation') || path.includes('/siparis-onay')) return 'order-confirmation';
    if (path.includes('/search') || path.includes('/arama')) return 'search';
    return 'other';
  }

  // ============================================
  // Standard E-commerce Tracking Methods
  // ============================================

  trackViewItem(items, extraData = {}) {
    if (!items || items.length === 0) return;
    
    const item = items[0]; // Usually single item on product page
    
    this.b2mTracker.track(this.eventTypes.VIEW_ITEM, {
      platform: this.platformName,
      item_id: item.item_id || item.id,
      item_name: item.item_name || item.name,
      item_brand: item.item_brand || item.brand,
      item_category: item.item_category || item.category,
      item_variant: item.item_variant || item.variant,
      price: item.price,
      currency: item.currency || 'TRY',
      ...extraData
    });
  }

  trackViewItemList(items, listName = '', extraData = {}) {
    if (!items || items.length === 0) return;
    
    this.b2mTracker.track(this.eventTypes.VIEW_ITEM_LIST, {
      platform: this.platformName,
      item_list_name: listName,
      items: items.map(item => ({
        item_id: item.item_id || item.id,
        item_name: item.item_name || item.name,
        item_brand: item.item_brand || item.brand,
        item_category: item.item_category || item.category,
        price: item.price,
        index: item.index
      })),
      item_count: items.length,
      ...extraData
    });
  }

  trackSelectItem(items, extraData = {}) {
    if (!items || items.length === 0) return;
    
    const item = items[0];
    
    this.b2mTracker.track(this.eventTypes.SELECT_ITEM, {
      platform: this.platformName,
      item_id: item.item_id || item.id,
      item_name: item.item_name || item.name,
      item_list_name: item.item_list_name,
      index: item.index,
      ...extraData
    });
  }

  trackAddToCart(items, value = null, currency = 'TRY', extraData = {}) {
    if (!items || items.length === 0) return;
    
    const totalValue = value || items.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
    
    this.b2mTracker.track(this.eventTypes.ADD_TO_CART, {
      platform: this.platformName,
      items: items.map(item => ({
        item_id: item.item_id || item.id,
        item_name: item.item_name || item.name,
        item_brand: item.item_brand || item.brand,
        item_category: item.item_category || item.category,
        item_variant: item.item_variant || item.variant,
        price: item.price,
        quantity: item.quantity || 1
      })),
      value: totalValue,
      currency: currency,
      ...extraData
    });
  }

  trackRemoveFromCart(items, extraData = {}) {
    if (!items || items.length === 0) return;
    
    this.b2mTracker.track(this.eventTypes.REMOVE_FROM_CART, {
      platform: this.platformName,
      items: items.map(item => ({
        item_id: item.item_id || item.id,
        item_name: item.item_name || item.name,
        price: item.price,
        quantity: item.quantity || 1
      })),
      ...extraData
    });
  }

  trackViewCart(items, value, currency = 'TRY', extraData = {}) {
    this.b2mTracker.track(this.eventTypes.VIEW_CART, {
      platform: this.platformName,
      items: items,
      value: value,
      currency: currency,
      item_count: items.length,
      ...extraData
    });
  }

  trackBeginCheckout(items, value, currency = 'TRY', extraData = {}) {
    this.b2mTracker.track(this.eventTypes.BEGIN_CHECKOUT, {
      platform: this.platformName,
      items: items,
      value: value,
      currency: currency,
      ...extraData
    });
  }

  trackAddShippingInfo(data, extraData = {}) {
    this.b2mTracker.track(this.eventTypes.ADD_SHIPPING_INFO, {
      platform: this.platformName,
      shipping_tier: data.shipping_tier,
      value: data.value,
      currency: data.currency || 'TRY',
      ...extraData
    });
  }

  trackAddPaymentInfo(data, extraData = {}) {
    this.b2mTracker.track(this.eventTypes.ADD_PAYMENT_INFO, {
      platform: this.platformName,
      payment_type: data.payment_type,
      value: data.value,
      currency: data.currency || 'TRY',
      ...extraData
    });
  }

  trackPurchase(data, extraData = {}) {
    this.b2mTracker.track(this.eventTypes.PURCHASE, {
      platform: this.platformName,
      transaction_id: data.transaction_id,
      value: data.value,
      currency: data.currency || 'TRY',
      tax: data.tax,
      shipping: data.shipping,
      coupon: data.coupon,
      items: data.items,
      ...extraData
    });
  }

  trackRefund(data, extraData = {}) {
    this.b2mTracker.track(this.eventTypes.REFUND, {
      platform: this.platformName,
      transaction_id: data.transaction_id,
      value: data.value,
      currency: data.currency || 'TRY',
      items: data.items,
      ...extraData
    });
  }

  trackAddToWishlist(items, extraData = {}) {
    if (!items || items.length === 0) return;
    
    this.b2mTracker.track(this.eventTypes.ADD_TO_WISHLIST, {
      platform: this.platformName,
      items: items,
      ...extraData
    });
  }

  trackSearch(searchTerm, extraData = {}) {
    this.b2mTracker.track(this.eventTypes.SEARCH, {
      platform: this.platformName,
      search_term: searchTerm,
      ...extraData
    });
  }

  // ============================================
  // Auto-tracking methods - override in subclasses
  // ============================================

  autoTrackProductPage() {
    // Override in platform-specific trackers
  }

  autoTrackCategoryPage() {
    // Override in platform-specific trackers
  }

  autoTrackCartPage() {
    // Override in platform-specific trackers
  }

  autoTrackCheckoutPage() {
    // Override in platform-specific trackers
  }

  autoTrackOrderConfirmation() {
    // Override in platform-specific trackers
  }

  autoTrackSearchPage() {
    // Override in platform-specific trackers
  }

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Normalize items from different formats to GA4 format
   */
  normalizeItems(items) {
    if (!items) return [];
    
    return items.map((item, index) => ({
      item_id: item.id || item.item_id || item.product_id || item.sku,
      item_name: item.name || item.item_name || item.product_name || item.title,
      item_brand: item.brand || item.item_brand || item.vendor,
      item_category: item.category || item.item_category || item.product_type,
      item_variant: item.variant || item.item_variant,
      price: parseFloat(item.price) || 0,
      quantity: parseInt(item.quantity) || 1,
      index: item.position || item.index || index
    }));
  }

  /**
   * Extract product data from DOM element
   */
  extractProductFromElement(element) {
    // Try various data attributes
    const product = {
      item_id: element.dataset.productId || element.dataset.id || element.dataset.sku,
      item_name: element.dataset.productName || element.dataset.name || element.dataset.title,
      price: parseFloat(element.dataset.price || element.dataset.productPrice) || 0,
      item_brand: element.dataset.brand || element.dataset.productBrand,
      item_category: element.dataset.category || element.dataset.productCategory,
      item_variant: element.dataset.variant || element.dataset.productVariant,
      quantity: parseInt(element.dataset.quantity) || 1
    };
    
    // If no data attributes, try to extract from child elements
    if (!product.item_name) {
      const nameEl = element.querySelector('.product-name, .product-title, [class*="name"], [class*="title"]');
      if (nameEl) product.item_name = nameEl.textContent.trim();
    }
    
    if (!product.price) {
      const priceEl = element.querySelector('.price, .product-price, [class*="price"]');
      if (priceEl) {
        const priceText = priceEl.textContent.replace(/[^\d.,]/g, '').replace(',', '.');
        product.price = parseFloat(priceText) || 0;
      }
    }
    
    return product;
  }

  /**
   * Parse price string to number
   */
  parsePrice(priceStr) {
    if (typeof priceStr === 'number') return priceStr;
    if (!priceStr) return 0;
    
    // Remove currency symbols and whitespace
    const cleaned = priceStr.toString()
      .replace(/[^\d.,]/g, '')
      .replace(/\.(?=.*\.)/g, '') // Keep only last dot
      .replace(',', '.');
    
    return parseFloat(cleaned) || 0;
  }
}
